import { useApp } from 'contexts/app';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Icon,
    IconButton,
    Paper,
    ResponsiveDialog,
    Stack,
    styled,
    Text,
    ViewMore,
} from 'shell/components';
import { getIconKind, getResourceName, getResourceType } from 'shell/components/ResourceChip';

const ContentBankResourcesUsageDialog = ({ open, submission, onClose }) => {
    const { currentAssignment } = useApp();

    return (
        <CustomResponsiveDialog open={open} scroll={'paper'} onClose={onClose}>
            <DialogTitle>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text component={'h1'} variant={'h6'} weight={'medium'}>
                        How was the content bank used?
                    </Text>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={onClose}
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Stack spacing={5}>
                    <Text color={'light'}>
                        The relevancy score indicates how closely the AI finds student work aligns with the material in
                        your uploaded documents and your specific instructions on its use.{' '}
                        <Text color={'light'} component={'span'} weight={'semibold'}>
                            Scores below 85% are disregarded.
                        </Text>
                    </Text>
                    <CustomPaper variant={'outlined'}>
                        {(submission.content_bank_resources_usage || [])
                            .filter(({ score }) => score >= 85)
                            .map(({ id, score }) => {
                                let resource = currentAssignment.content_bank_resources.find(
                                    ({ id: targetId }) => targetId === id
                                );
                                return (
                                    <ResourceStack key={id}>
                                        <Stack spacing={4}>
                                            <Stack direction={'row'} spacing={3}>
                                                <CustomIcon kind={getIconKind('file', resource)} />
                                                <Stack>
                                                    <Text align={'left'}>{getResourceName('file', resource)}</Text>
                                                    <Text color={'light'}>{getResourceType('file', resource)}</Text>
                                                </Stack>
                                            </Stack>
                                            <Text>
                                                Relevancy score:{' '}
                                                <Text color={'light'} component={'span'}>
                                                    {score}%
                                                </Text>
                                            </Text>
                                            <ViewMore
                                                text={
                                                    <>
                                                        Instructions for the AI:{' '}
                                                        <Text color={'light'} component={'span'}>
                                                            {resource.description
                                                                ? `“${resource.description}”`
                                                                : 'None'}
                                                        </Text>
                                                    </>
                                                }
                                            />
                                        </Stack>
                                    </ResourceStack>
                                );
                            })}
                    </CustomPaper>
                </Stack>
            </DialogContent>
            <Divider />
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </CustomResponsiveDialog>
    );
};

const CustomResponsiveDialog = styled(ResponsiveDialog)(({ theme }) => {
    return {
        zIndex: theme.zIndex.drawer + 2,
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            maxWidth: '700px',
            width: '100%',
            height: '100%',
        },
    };
});

const CustomPaper = styled(Paper)(() => {
    return {
        padding: 0,
    };
});

const ResourceStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(5),
        '&:nth-child(odd)': {
            background: theme.palette.ensightful.neutral[50],
        },
        '&:nth-child(even)': {
            background: theme.palette.ensightful.neutral[100],
        },
        '&:first-child': {
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
        },
        '&:last-child': {
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px',
        },
    };
});

const CustomIcon = styled(Icon)(() => {
    return {
        fontSize: '2rem',
    };
});

export default ContentBankResourcesUsageDialog;
